import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import { createTaskRequest, updateTaskRequest } from '@signalvote/shared';
import type { CreateTaskRequest, UpdateTaskRequest, TaskListResponse, TaskListItemDto } from '@signalvote/shared';
import type { TaskListParams } from '@/lib/queryKeys';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';
import { useAuth } from '@/contexts/AuthContext';

const api = getApiClient();

/**
 * Fetch tasks with optional filters
 */
export function useTasks(params?: TaskListParams) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.tasks.list(params),
    queryFn: () => api.tasks.getTasks(params),
    enabled: !!selectedCampaign,
  });
}

type NewTask = Pick<CreateTaskRequest, 'title'> &
  Partial<CreateTaskRequest> & { assignedToName?: string; categoryName?: string };

/**
 * Create a new task with optimistic updates.
 * Accepts optional `assignedToName` for display in the optimistic entry.
 */
export function useCreateTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (data: NewTask) => {
      // Strip display-only fields before sending to API
      const { assignedToName, categoryName, ...request } = data;
      return api.tasks.createTask(createTaskRequest(request));
    },
    onMutate: async (newTask) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks.all });

      const previousQueries = queryClient.getQueriesData<TaskListResponse>({
        queryKey: queryKeys.tasks.all,
      });

      const request = createTaskRequest(newTask);
      const optimisticItem: TaskListItemDto = {
        id: `temp-${Date.now()}`,
        title: request.title,
        source: request.source,
        status: 'Pending',
        dueDate: request.dueDate,
        dueTime: request.dueTime,
        durationMinutes: request.durationMinutes,
        assignedToId: request.assignedToId,
        assignedToName: newTask.assignedToName ?? null,
        categoryId: request.categoryId,
        categoryName: newTask.categoryName ?? null,
        actionUrl: request.actionUrl,
        isOpen: !request.assignedToId,
        acknowledgedAt: null,
        createdById: user?.id ?? null,
        createdByName: user ? `${user.firstName} ${user.lastName}` : null,
      };

      queryClient.setQueriesData<TaskListResponse>(
        { queryKey: queryKeys.tasks.all },
        (old) => {
          if (!old || !('items' in old)) return old;
          return {
            ...old,
            items: [optimisticItem, ...old.items],
            totalCount: old.totalCount + 1,
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _newTask, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.unacknowledgedCount() });
    },
  });
}

/**
 * Update an existing task
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UpdateTaskRequest> }) =>
      api.tasks.updateTask(id, updateTaskRequest(data)),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Delete a task
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.tasks.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

/**
 * Accept an assigned task
 */
export function useAcceptTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.tasks.acceptTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

/**
 * Decline an assigned task
 */
export function useDeclineTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.tasks.declineTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

/**
 * Complete a task with optimistic cache update for instant sidebar reflection.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.tasks.completeTask(id),
    onMutate: async (taskId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks.all });

      const previousQueries = queryClient.getQueriesData<TaskListResponse>({
        queryKey: queryKeys.tasks.all,
      });

      queryClient.setQueriesData<TaskListResponse>(
        { queryKey: queryKeys.tasks.all },
        (old) => {
          if (!old || !('items' in old)) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.id === taskId ? { ...item, status: 'Completed' } : item
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _taskId, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

/**
 * Reopen a completed task back to Pending with optimistic cache update.
 */
export function useReopenTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.tasks.reopenTask(id),
    onMutate: async (taskId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks.all });

      const previousQueries = queryClient.getQueriesData<TaskListResponse>({
        queryKey: queryKeys.tasks.all,
      });

      queryClient.setQueriesData<TaskListResponse>(
        { queryKey: queryKeys.tasks.all },
        (old) => {
          if (!old || !('items' in old)) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.id === taskId ? { ...item, status: 'Pending' } : item
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _taskId, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

/**
 * Fetch count of unacknowledged inbound task assignments.
 * Polls every 60s for the header badge indicator.
 */
export function useUnacknowledgedTaskCount() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.tasks.unacknowledgedCount(),
    queryFn: () => api.tasks.getUnacknowledgedCount(),
    enabled: !!selectedCampaign,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Acknowledge a task (mark as seen by the assignee).
 * Invalidates both the task list and unacknowledged count.
 */
export function useAcknowledgeTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.tasks.acknowledgeTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.unacknowledgedCount() });
    },
  });
}
